import { CalculationResult } from "../../types/calculations";
import { Drive, DriveType, DriveWithCalculations } from "../../types/drive";
import {
  getDriveCost,
  getDriveTons,
  getDrivePower,
  getDriveMaintenance,
} from "../../constants/drives";

/**
 * Input parameters for calculating drive stats.
 */
export interface DriveStatsInput {
  drives: Drive[];
  shipSize: number;
}

/**
 * Detailed breakdown of the drive stats.
 */
export interface DriveStatsOutput {
  // Cost
  totalCost: number;
  maneuverDriveCost: number;
  jumpDriveCost: number;
  reactionDriveCost: number;
  // Volume
  totalConsumedVolume: number;
  maneuverDriveVolume: number;
  jumpDriveVolume: number;
  reactionDriveVolume: number;
}

/**
 * Converts a minimal Drive object to a DriveWithCalculations object
 * for display purposes by computing all the calculated fields
 */
export const calculateDriveWithValues = (
  drive: Drive,
  shipSize: number,
): DriveWithCalculations => {
  const tons = getDriveTons(drive.type, drive.rating, shipSize);
  const cost = getDriveCost(drive.type, drive.rating, shipSize);

  return {
    ...drive,
    tons,
    cost,
    weight: tons,
    power: getDrivePower(drive.type, drive.rating),
    maintenanceCost: getDriveMaintenance(drive.type, drive.rating, shipSize),
  };
};

/**
 * Converts an array of minimal Drive objects to DriveWithCalculations objects
 */
export const calculateDrivesWithValues = (
  drives: Drive[],
  shipSize: number,
): DriveWithCalculations[] => {
  return drives.map((drive) => calculateDriveWithValues(drive, shipSize));
};

/**
 * Calculates the total cost and volume of the selected drives.
 */
export const calculateDriveStats = (
  input: DriveStatsInput,
): CalculationResult<DriveStatsOutput> => {
  try {
    const { drives, shipSize } = input;
    const drivesWithValues = calculateDrivesWithValues(drives, shipSize);

    let maneuverDriveCost = 0;
    let jumpDriveCost = 0;
    let reactionDriveCost = 0;
    let maneuverDriveVolume = 0;
    let jumpDriveVolume = 0;
    let reactionDriveVolume = 0;

    for (const drive of drivesWithValues) {
      const tons = getDriveTons(drive.type, drive.rating, shipSize);
      const cost = getDriveCost(drive.type, drive.rating, shipSize);
      switch (drive.type) {
        case DriveType.MANEUVER:
          maneuverDriveCost += cost;
          maneuverDriveVolume += tons;
          break;
        case DriveType.JUMP:
          jumpDriveCost += cost;
          jumpDriveVolume += tons;
          break;
        case DriveType.REACTION:
          reactionDriveCost += cost;
          reactionDriveVolume += tons;
          break;
      }
    }

    const totalCost = maneuverDriveCost + jumpDriveCost + reactionDriveCost;
    const totalConsumedVolume =
      maneuverDriveVolume + jumpDriveVolume + reactionDriveVolume;

    return {
      success: true,
      data: {
        totalCost,
        maneuverDriveCost,
        jumpDriveCost,
        reactionDriveCost,
        totalConsumedVolume,
        maneuverDriveVolume,
        jumpDriveVolume,
        reactionDriveVolume,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate drive stats: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};
